<template>
   <div class="row">
      <div class="sticky">
         <!-- FILTER HEADER -->
         <div class="col-md-12 mb-3 filter-header">
            <h1 aria-label="Filter your job search">
               Filter
               <small v-if="resultCount">{{ resultCount }} results</small>
               <small v-if="isFiltered"
                  ><a href="categories" aria-label="Clear all filters" @click.prevent="clearAll()"
                     >clear all</a
                  ></small
               >
            </h1>
         </div>

         <!-- FILTER ACCORDION -->
         <div id="card-filters">
            <!-- key competencies -->
            <div class="card card-dark">
               <div
                  v-popover
                  ref="tooltip"
                  class="card-header"
                  data-container="body"
                  data-toggle="popover"
                  data-trigger="hover"
                  data-placement="bottom"
                  :data-content="tooltips[0][collapsedStateIndex(filterCompetencies)]"
               >
                  <button
                     id="filter-competencies"
                     class="card-link"
                     data-toggle="collapse"
                     data-parent="#card-filters"
                     href="#card-element-competencies"
                     aria-controls="card-element-competencies"
                     aria-label="Filter by Key competencies"
                     aria-expanded="true"
                     @click="toggleTooltip"
                  >
                     Key competencies
                  </button>
               </div>
               <div
                  id="card-element-competencies"
                  :class="['collapse', collapsedStateClass(filterCompetencies)]"
               >
                  <div class="card-body" role="list">
                     <div v-if="loading" class="empty">
                        <div class="spinner-border text-secondary" role="status">
                           <span class="sr-only">Loading</span>
                        </div>
                        <h1>Loading</h1>
                     </div>
                     <FilterItem
                        v-for="filter in competencies"
                        v-if="filter.isKeyCompetency"
                        :key="filter.competencyId"
                        :id="filter.competencyId"
                        :label="filter.competency1"
                        :secondary="filter.competencyDescription"
                        group="competency"
                     />
                  </div>
               </div>
            </div>

            <!-- other competencies -->
            <div class="card">
               <div
                  v-popover
                  ref="tooltip"
                  class="card-header"
                  data-container="body"
                  data-toggle="popover"
                  data-trigger="hover"
                  data-placement="bottom"
                  :data-content="tooltips[1][collapsedStateIndex(filterOther)]"
               >
                  <button
                     id="filter-other"
                     class="card-link"
                     data-toggle="collapse"
                     data-parent="#card-filters"
                     href="#card-element-other"
                     aria-controls="card-element-other"
                     aria-label="Filter by Other competencies"
                     @click="toggleTooltip"
                  >
                     Other competencies
                  </button>
               </div>
               <div id="card-element-other" :class="['collapse', collapsedStateClass(filterOther)]">
                  <div class="card-body" role="list">
                     <FilterItem
                        v-for="filter in competencies"
                        v-if="!filter.isKeyCompetency"
                        :key="filter.competencyId"
                        :id="filter.competencyId"
                        :label="filter.competency1"
                        :secondary="filter.competencyDescription"
                        group="competency"
                     />
                  </div>
               </div>
            </div>

            <!-- job level -->
            <div class="card">
               <div
                  v-popover
                  ref="tooltip"
                  class="card-header"
                  data-container="body"
                  data-toggle="popover"
                  data-trigger="hover"
                  data-placement="bottom"
                  :data-content="tooltips[2][collapsedStateIndex(filterLevel)]"
               >
                  <button
                     id="filter-level"
                     class="card-link"
                     data-toggle="collapse"
                     data-parent="#card-filters"
                     href="#card-element-level"
                     aria-controls="card-element-level"
                     aria-label="Filter by Job level"
                     @click="toggleTooltip"
                  >
                     Job level
                  </button>
               </div>
               <div id="card-element-level" :class="['collapse', collapsedStateClass(filterLevel)]">
                  <div class="card-body" role="list">
                     <FilterItem
                        v-for="filter in jobLevels"
                        :key="filter.jobLevelId"
                        :id="filter.jobLevelId"
                        :label="filter.jobLevel1"
                        :secondary="filter.jobLevelDescription"
                        group="level"
                     />
                  </div>
               </div>
            </div>

            <!-- educational requirements -->
            <div class="card">
               <div
                  v-popover
                  ref="tooltip"
                  class="card-header"
                  data-container="body"
                  data-toggle="popover"
                  data-trigger="hover"
                  data-placement="bottom"
                  :data-content="tooltips[3][collapsedStateIndex(filterEducation)]"
               >
                  <button
                     id="filter-education"
                     class="card-link"
                     data-toggle="collapse"
                     data-parent="#card-filters"
                     href="#card-element-education"
                     aria-controls="card-element-education"
                     aria-label="Filter by Educational requirements"
                     @click="toggleTooltip"
                  >
                     Minimum education
                  </button>
               </div>
               <div
                  id="card-element-education"
                  :class="['collapse', collapsedStateClass(filterEducation)]"
               >
                  <div class="card-body" role="list">
                     <FilterItem
                        v-for="filter in education"
                        :key="filter.minEdReqId"
                        :id="filter.minEdReqId"
                        :label="filter.minimumEducationRequirement"
                        :secondary="null"
                        group="education"
                     />
                  </div>
               </div>
            </div>

            <!-- job domains -->
            <div v-if="showDomains" class="card">
               <div
                  v-popover
                  ref="tooltip"
                  class="card-header"
                  data-container="body"
                  data-toggle="popover"
                  data-trigger="hover"
                  data-placement="bottom"
                  :data-content="tooltips[4][collapsedStateIndex(filterDomains)]"
               >
                  <button
                     id="{this.filterDomains}"
                     class="card-link"
                     data-toggle="collapse"
                     data-parent="#card-filters"
                     href="#card-element-domains"
                     aria-controls="card-element-domains"
                     aria-label="Filter by Job Domain"
                     @click="toggleTooltip"
                  >
                     Job domain
                  </button>
               </div>
               <div
                  id="card-element-domains"
                  :class="['collapse', collapsedStateClass(filterDomains)]"
               >
                  <div class="card-body" role="list">
                     <FilterItem
                        v-for="filter in jobDomains"
                        :key="filter.jobDomainId"
                        :id="filter.jobDomainId"
                        :label="filter.jobDomain1"
                        :secondary="filter.jobDomainDescription"
                        :short-domain-name="filter.shortName"
                        group="domain"
                     />
                  </div>
               </div>
            </div>
            <!-- powered by Uptime -->
            <!--
            <div class="powered-by">
               Powered by
               <img
                  src="~@/assets/images/uptime-logo.png"
                  alt="Uptime Institute logo"
                  class="logo"
               />
            </div>
            -->
         </div>
      </div>
   </div>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import Vue from 'vue';
import {reduce} from 'lodash';
import {parse} from 'query-string';
import FilterItem from '@/components/FilterItem';
import $ from 'jquery';

export default {
   name: 'FilterGroups',
   components: {
      FilterItem,
   },
   props: {
      showDomains: Boolean,
   },
   data: function() {
      return {
         resultCount: 0,
         loading: false,
         isFiltered: false,
         competencies: [],
         jobLevels: [],
         education: [],
         jobDomains: [],
         tooltips: [
            [
               'Click to view key competency filters',
               'Check boxes to filter job roles on key competencies',
            ],
            [
               'Click to view other competency filters',
               'Check boxes to filter job roles on other competencies',
            ],
            ['Click to view job level filters', 'Check boxes to filter job roles on job level'],
            [
               'Click to view minimum education filters',
               'Check boxes to filter job roles on education',
            ],
            ['Click to view job domain filters', 'Check boxes to filter job roles on job domain'],
         ],
         /*
         filterCompetenciesIndex: 1,
         filterOtherIndex: 0,
         filterLevelIndex: 0,
         filterEducationIndex: 0,
         filterJobDomain: 0,
         */
      };
   },
   created: function() {
      this.filterCompetencies = 'filter-competencies';
      this.filterOther = 'filter-other';
      this.filterLevel = 'filter-level';
      this.filterEducation = 'filter-education';
      this.filterDomains = 'filter-domains';
   },
   mounted: function() {
      // if any filters are present on URL while loading page,
      // use them to initialize filters
      const query = parse(window.location.search);

      // load filters from database and initialize state
      this.loading = true;
      this.$apiGet('Competencies').then(response => {
         this.competencies = response.data;
         // convert array to object using IDs as keys
         Vue.set(
            this.$store.state.filters,
            'competency',
            reduce(
               this.competencies,
               (acc, {competencyId, competency1}) => ({
                  ...acc,
                  [competencyId]: {
                     id: competencyId,
                     checked: this.hasFilter(query, 'competency', competencyId),
                     label: competency1,
                  },
               }),
               {}
            )
         );
         this.loading = false;
      });
      this.$apiGet('JobLevels').then(response => {
         this.jobLevels = response.data;
         Vue.set(
            this.$store.state.filters,
            'level',
            reduce(
               this.jobLevels,
               (acc, {jobLevelId, jobLevel1}) => ({
                  ...acc,
                  [jobLevelId]: {
                     id: jobLevelId,
                     checked: this.hasFilter(query, 'jobLevel', jobLevelId),
                     label: jobLevel1,
                  },
               }),
               {}
            )
         );
      });
      this.$apiGet('Education').then(response => {
         this.education = response.data;
         Vue.set(
            this.$store.state.filters,
            'education',
            reduce(
               this.education,
               (acc, {minEdReqId, minimumEducationRequirement}) => ({
                  ...acc,
                  [minEdReqId]: {
                     id: minEdReqId,
                     checked: this.hasFilter(query, 'education', minEdReqId),
                     label: minimumEducationRequirement,
                  },
               }),
               {}
            )
         );
      });
      this.$apiGet('JobDomains').then(response => {
         this.jobDomains = response.data;
         // pass all domain data to CategoriesPage
         this.$emit('load-domains', this.jobDomains);
         Vue.set(
            this.$store.state.filters,
            'domain',
            reduce(
               this.jobDomains,
               (acc, {jobDomainId, jobDomain1, shortName}) => ({
                  ...acc,
                  [jobDomainId]: {
                     id: jobDomainId,
                     checked: this.hasFilter(query, 'jobDomain', jobDomainId),
                     label: jobDomain1,
                     className: `cat-${shortName}`,
                  },
               }),
               {}
            )
         );
      });
   },
   methods: {
      hasFilter: function(query, group, id) {
         const parm = query[group];
         const val = String(id);
         if (Array.isArray(parm)) {
            return parm.includes(val);
         }
         return parm === val;
      },
      clearAll: function() {
         this.$store.commit('filters/clearAll');
         this.$router.push({path: 'categories'});
      },
      toggleTooltip(evt) {
         // console.log('toggle tool tip', evt.target.id);
         // use the target id to store collapsed toggle in store
         var toggleValue = this.$store.state.filters.collapsedState[evt.target.id];
         toggleValue ^= 1;
         this.$store.commit('filters/setCollapsedState', {id: evt.target.id, val: toggleValue});
         const tt = $(evt.target).parent();
         tt.popover('hide');
         setTimeout(function() {
            // show the popover after a short delay
            // forces it to update to the new text
            tt.popover('show');
         }, 100);
      },
      collapsedStateIndex(id) {
         var val = this.$store.state.filters.collapsedState[id];
         // console.log('check state for ', id, val);
         return val;
      },
      collapsedStateClass(id) {
         var val = this.$store.state.filters.collapsedState[id];
         return val ? 'show' : '';
      },
   },
};
</script>



// WEBPACK FOOTER //
// src/components/FilterGroups.vue